import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { get, isEmpty, head, find, findIndex, groupBy, map, sortBy, values } from 'lodash'

import MenuWithConfirmationModal from '../MenuWithConfirmationModal'
import Menu from '../Menu'
import CategoryItems from '../CategoryItems'
import Category from '../Menu/Category'
import Cart from '../Cart'
import { getUniqCategoriesWithExistingItems } from '../../selectors/categories'
import { getSelectedMenusIds } from '../../selectors/config'
import { getCartItems } from '../../selectors/cart'
import { getCarouselImages } from '../../actions/carouselImages'

import UserIdleMonitor from '../utils/UserIdleMonitor'
import Slider from '../components/Slider'
import CartItemDetailsKiosk from '../CartItemDetailKiosk'
import { getItemsForCurrentMenu } from '../../selectors/items'
import { safelyUpdateCartItemQuantity, removeFromCart } from '../../actions/cart';

export class NewOrder extends Component {
  static propTypes = {
    categories: PropTypes.array.isRequired,
  }

  state = {
    selectedCategoryId: null,
    categorySetFromMenu: false,
    modeName: 'Add to cart',
    isSidebarOpen: false,
    editedIndex: undefined,
    isSidebarEditMode: false,
    cartItemsSummary: []
  }

  viewedItems = []

  constructor(props) {
    super(props)
    this.categoryRefs = {}
    this.categoryLinkRefs = {}
    this.categoriesListingRef = React.createRef()
    this.idleMonitor = new UserIdleMonitor(process.env.REACT_APP_DEV_IDLE_TIMEOUT || 30000)
  }

  componentDidMount() {
    this.initActiveCategory()
    document.title = 'New order'
    this.idleMonitor.addEventListeners()
    this.props.getCarouselImages()
  }

  componentWillUnmount() {
    this.idleMonitor.removeEventListeners()
  }

  handleScroll = () => {
    if (this.state.categorySetFromMenu) {
      this.setState({ categorySetFromMenu: false })
      return
    }
    const categoriesList = this.categoriesListingRef.current.getBoundingClientRect()
    const category = find(this.props.categories, category => {
      const refDimensions = this.categoryRefs[category.itemCategoryUuid].getBoundingClientRect()
      return (
        refDimensions.top >= categoriesList.top ||
        (refDimensions.bottom - 50 >= categoriesList.top &&
          refDimensions.bottom - 50 <= categoriesList.bottom) ||
        (refDimensions.top < categoriesList.top && refDimensions.bottom > categoriesList.bottom)
      )
    })
    if (category && this.state.selectedCategoryId !== category.itemCategoryUuid) {
      this.setState({ selectedCategoryId: category.itemCategoryUuid })
      this.categoryLinkRefs[category.itemCategoryUuid].scrollIntoView()
    }
  }

  componentDidUpdate() {
    this.initActiveCategory()
  }

  initActiveCategory = () => {
    const { categories } = this.props
    if (!this.state.selectedCategoryId && !isEmpty(categories)) {
      this.setState({ selectedCategoryId: head(categories).itemCategoryUuid })
    }
  }

  onSelectCategory = category => {
    this.setState({ selectedCategoryId: category.itemCategoryUuid, categorySetFromMenu: true })
    this.categoryRefs[category.itemCategoryUuid].scrollIntoView()
  }

  handleViewportItemEnter = item => {
    this.viewedItems.push(item)
  }

  renderMenu = () => {
    const { selectedMenuIds, cartItems } = this.props
    const displayConfirmationModal = (selectedMenuIds.length > 1) && !isEmpty(cartItems)
    const redirectTo = selectedMenuIds.length > 1
      ? '/kiosk/menu_select'
      : '/kiosk/attract_loop'

    return displayConfirmationModal
      ? <MenuWithConfirmationModal redirectTo={redirectTo} />
      : <Menu redirectTo={redirectTo} />
  }

  toggleSidebar = isOpen => this.setState({isSidebarOpen: isOpen, isSidebarEditMode: false})

  editItem = (itemId, index) => {
    const modifierGroups = get(this.props.items, ['byId', itemId, 'modifierGroups'])

    if (!isEmpty(modifierGroups)) {
      this.setState({
        editedIndex: index,
        isSidebarOpen: true,
        isSidebarEditMode: true,
      })
    }
  }

  resetEditedIndex = () => this.setState({editedIndex: undefined})

  setModifiersMode = modeName => this.setState({sidebarMode: modeName})

  _generateGroupKeyForCartItem = cartItem => {
    const { itemId, modifiers } = cartItem
    const sortedModifiers = sortBy(modifiers, 'id')
    return sortedModifiers.reduce((result, modifierObject) => (result + get(modifierObject, 'id', '')), itemId)
  }

  mergeQuantityWithIdenticalItemInCart = () => {
    const { cartItems, removeFromCart, safelyUpdateCartItemQuantity } = this.props
    const identicalItemsGroups = map(groupBy(cartItems, this._generateGroupKeyForCartItem), group => values(group))

    identicalItemsGroups.forEach(itemsGroup => {
      if (itemsGroup.length > 1) {
        const [firstItem, ...itemsWithoutFirstItem] = itemsGroup
        const firstItemKey = this._generateGroupKeyForCartItem(firstItem)
        const firstItemQuantity = get(firstItem, 'quantity', 0)
        const firstItemIndex = findIndex(cartItems, cartItem => this._generateGroupKeyForCartItem(cartItem) === firstItemKey)
        itemsWithoutFirstItem.forEach(item => {
          const currentItemQuantity = get(item, 'quantity', 0)
          const currentItemIndex = findIndex(
            cartItems,
            cartItem => this._generateGroupKeyForCartItem(cartItem) === firstItemKey,
            firstItemIndex + 1,
          )
          removeFromCart({ index: currentItemIndex })
          safelyUpdateCartItemQuantity({
            index: firstItemIndex,
            quantity: firstItemQuantity + currentItemQuantity,
          })
        })
      }
    })
  }

  setItemsSummary = cartItemsSummary => {
    this.setState({ cartItemsSummary })
  }

  render() {
    const { categories } = this.props
    const { selectedCategoryId, editedIndex, isSidebarOpen, isSidebarEditMode } = this.state

    return (
      <>
        <CartItemDetailsKiosk
          mergeQuantityWithIdenticalItemInCart={this.mergeQuantityWithIdenticalItemInCart}
          resetEditedIndex={this.resetEditedIndex}
          isSidebarOpen={isSidebarOpen}
          toggleSidebar={this.toggleSidebar}
          cartItemIndex={editedIndex}
          isSidebarEditMode={isSidebarEditMode}
        />
        <div className="order">
          <div className="order__main">
            {this.renderMenu()}
            <div className="menu">
              <Slider>
                {categories.map(category => (
                  <Category
                    category={category}
                    isActive={selectedCategoryId === category.itemCategoryUuid}
                    key={category.itemCategoryUuid}
                    onSelect={() => this.onSelectCategory(category)}
                    refProp={node => {
                      this.categoryLinkRefs[category.itemCategoryUuid] = node
                    }}
                  />
                ))}
              </Slider>
            </div>
            <div className="categories" onScroll={this.handleScroll} ref={this.categoriesListingRef}>
              {categories.map(category => (
                <CategoryItems
                  setItemsSummary={this.setItemsSummary}
                  category={category}
                  toggleSidebar={this.toggleSidebar}
                  key={category.itemCategoryUuid}
                  onViewportItemEnter={this.handleViewportItemEnter}
                  refProp={node => {
                    this.categoryRefs[category.itemCategoryUuid] = node
                  }}
                  scrollableAncestorRef={this.categoriesListingRef}
                />
              ))}
            </div>
          </div>
          <Cart
            setItemsSummary={this.setItemsSummary}
            cartItemsSummary={this.state.cartItemsSummary}
            onItemClick={this.editItem} />
        </div>
      </>
    )
  }
}

const mapStateToProps = state => ({
  items: getItemsForCurrentMenu(state),
  categories: getUniqCategoriesWithExistingItems(state),
  selectedMenuIds: getSelectedMenusIds(state),
  cartItems: getCartItems(state),
})

const mapDispatchToProps = {
  getCarouselImages,
  safelyUpdateCartItemQuantity,
  removeFromCart,
}

export default connect(mapStateToProps, mapDispatchToProps)(NewOrder)
